"use client";

import { useLayoutEffect, useRef, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  media: ReactNode;
  header: ReactNode;
};

// Photo column CSS var this component writes and QuizStepConstraints/
// QuizStepPriorities never touch — the box's own max-width cap (see the
// header-height effect below). Falls back to "none" (today's behaviour)
// until the first measurement lands.
const PHOTO_CAP_VAR = "--bsa-quiz-photo-cap";

/**
 * Photo/form split — "Version 2" of the design handoff at Extra Files/
 * Claude Design - Handoff Files/design_handoff_lodge_matching_quiz 2/
 * design_handoff_quiz_step_header_over_photo/README.md.
 *
 * Keeps Option D's split, sticky photo column and fixed 3:2 box, but moves
 * the step header into that column above the photo (md+ only — mobile keeps
 * the header in the form column) and drops the full-viewport centring that
 * pushed the whole step ~230px down the page.
 *
 * Header placement mechanism: on mobile the "photo column" is `contents` —
 * header, form and photo box are direct children of the outer flex column,
 * ordered via `order` (header, form, photo). At md+ the column becomes a
 * real sticky flex item and the header sits above the photo inside it. This
 * keeps `header` (and `media`) mounted exactly once — CSS relocates them,
 * nothing is duplicated across breakpoints (a duplicated header/media pair
 * was tried and reverted).
 */
export function PhotoSplit({ children, media, header }: Props) {
  const headerRef = useRef<HTMLDivElement>(null);
  const photoRef = useRef<HTMLDivElement>(null);

  // Cap the photo box's WIDTH (never height — that would change the box's
  // ratio with the viewport and silently invalidate every hand-judged
  // anchor in quizImageAnchors.ts) so its derived 3:2 height can never
  // exceed the vertical slack above the fold. See README §6.
  useLayoutEffect(() => {
    const headerEl = headerRef.current;
    const photoEl = photoRef.current;
    if (!headerEl || !photoEl) return;

    function recompute() {
      if (!headerEl || !photoEl) return;
      if (window.innerWidth < 768) {
        photoEl.style.removeProperty(PHOTO_CAP_VAR);
        return;
      }
      // Measure the photo box's actual top directly, rather than
      // reconstructing it from the nav height + header height + the
      // header's own margins. getBoundingClientRect() never includes
      // margins, so the old reconstruction silently dropped the header's
      // negative top margin and its mb-5 — a net +12px error that let the
      // capped photo grow past the fold on short viewports.
      // Measuring photoEl.top directly also sidesteps hardcoding
      // --bsa-nav-h's pixel value, which was wrong for anyone with a
      // non-16px root font size.
      const above = photoEl.getBoundingClientRect().top;
      const slack = Math.max(200, window.innerHeight - above);
      photoEl.style.setProperty(PHOTO_CAP_VAR, `${Math.round(slack * 1.5)}px`);
    }

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(headerEl);
    window.addEventListener("resize", recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, []);

  return (
    <div className="flex flex-col md:grid md:grid-cols-2 md:min-h-[calc(100vh-var(--bsa-nav-h))]">
      {/* Photo column. Mobile: `contents` so header/photo/form all sit as
          direct children of the outer flex column and `order` interleaves
          them. md+: a real sticky flex column, pinned under the nav while
          the form scrolls — `height: auto`, no `justify-center` (dropped
          from Option D per README §2: the full-viewport height only ever
          existed to support a centering the new layout doesn't want, and
          otherwise reserves a viewport of unused row height).

          md:self-start deliberately opts this column OUT of the grid row's
          stretch (the row is now viewport-height, via md:min-h-[...] above,
          so the two steps' NavFooters land at the same y — see
          NavFooter.tsx). Without self-start this column would stretch to
          match the taller form column, and a sticky element as tall as its
          own containing block can never actually stick — on any viewport
          where the form overflows, the photo would scroll away instead of
          pinning. self-start keeps it at content height, which is also
          exactly what it looked like before this row became tall.

          md:pt-0: the title sits right at the top of the column, flush
          under the nav. This is now DELIBERATELY smaller than the form
          column's own top offset (md:pt-24 — see QuizStepShell.tsx): Nic
          asked for the title to start higher while the first form field
          starts lower, so the two columns' top offsets are no longer in
          sync on purpose (a second, explicit reversal of the header-over-
          photo README on this branch, the first being NavFooter's anchor
          point — don't "fix" this back into alignment). */}
      <div className="contents md:flex md:flex-col md:order-1 md:sticky md:top-[var(--bsa-nav-h)] md:self-start md:pt-0">
        {/* Header: mobile order-1 (first, in the flow, with its own top
            padding matching the form's old top padding); md+ sits above
            the photo box in this column, indented 24px so the title aligns
            with the nav logo even though the photo below it is edge-to-edge
            — that misalignment is intentional (README §1).

            md:pt-0, no negative margin: an earlier round tried clawing back
            a further 8px via md:-mt-2 to nudge the title up past flush, but
            that pulled the eyebrow's line box up into the fixed progress
            bar's territory (QuizWizard.tsx, top-[var(--bsa-nav-h)]) —
            visible as the bar crossing "STEP 0X OF 0X".
            md:pt-0 is the real ceiling on this side, not md:-mt-2 as
            previously documented; the bulk of "line the first input up
            with the subheading" comes from the form column's own offset
            instead (see QuizStepShell.tsx's md:pt-24). */}
        <div
          ref={headerRef}
          className="order-1 px-6 pt-6 sm:px-8 sm:pt-8 lg:px-10 lg:pt-10 md:order-1 md:px-6 md:pt-0 md:pb-0 mb-2 md:mb-5"
        >
          {header}
        </div>

        {/* Photo box: h-60 (240px) on mobile; a fixed aspect-[3/2] on
            desktop, flex-none so the column doesn't stretch or shrink it.
            md:mr-auto keeps it flush to the left/viewport edge as its
            max-width cap kicks in on very wide screens — surface fills the
            remainder of the column. bg-bush-100 is the pre-load background. */}
        <div
          ref={photoRef}
          className="order-3 relative h-60 overflow-hidden bg-bush-100 md:h-auto md:w-full md:flex-none md:aspect-[3/2] md:mr-auto md:max-w-[var(--bsa-quiz-photo-cap,none)]"
        >
          <div className="relative h-full w-full">{media}</div>
        </div>
      </div>

      {/* Form side — second on mobile (after the header), right on desktop */}
      <div className="order-2 flex flex-col md:order-2">{children}</div>
    </div>
  );
}
